import {
  FC, memo, useCallback, useState,
} from 'react';
import { App, Button, Space } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { attachmentApi } from 'entities/attachment/api/attachmentApi';
import { useGlobalSpinner } from 'shared/hooks';
import { Text } from 'shared/ui/Typography';

export interface AttachmentDownloadListProps {
  /** UUID вложений. Порядок сохраняется, нумерация в подписях от 1. */
  attachmentUuids?: string[];
  label?: string;
  emptyText?: string;
  disabled?: boolean;
}

/**
 * Список вложений со скачиванием по presigned-ссылке.
 * Только просмотр: удалять и добавлять файлы здесь нельзя.
 */
export const AttachmentDownloadList: FC<AttachmentDownloadListProps> = memo(({
  attachmentUuids,
  label = 'Вложения',
  emptyText = 'Вложений нет',
  disabled = false,
}) => {
  const { message } = App.useApp();
  const { showSpinner, hideSpinner } = useGlobalSpinner();
  const [downloadingUuid, setDownloadingUuid] = useState<string | null>(null);

  const handleDownload = useCallback(async (uuid: string) => {
    try {
      setDownloadingUuid(uuid);
      showSpinner('Получение ссылки для скачивания...');

      const { downloadUrl } = await attachmentApi.getDownloadUrl(uuid);
      if (!downloadUrl) {
        throw new Error('Не удалось получить ссылку для скачивания');
      }

      // Имя файла задаёт бэк через Content-Disposition в presigned-ссылке.
      // Своё имя тут подставлять нельзя: оно затрёт оригинальное, а без него
      // браузер сохранял файл под UUID из ключа объекта в MinIO.
      window.location.assign(downloadUrl);
    } catch (error) {
      console.error('Ошибка скачивания файла:', error);
      message.error('Ошибка скачивания файла');
    } finally {
      setDownloadingUuid(null);
      hideSpinner();
    }
  }, [showSpinner, hideSpinner, message]);

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={4}>
      <Text strong>{label}</Text>
      {!attachmentUuids?.length ? (
        <Text type="secondary">{emptyText}</Text>
      ) : (
        attachmentUuids.map((uuid, index) => (
          <Button
            key={uuid}
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(uuid)}
            loading={downloadingUuid === uuid}
            disabled={disabled || (!!downloadingUuid && downloadingUuid !== uuid)}
            block
            style={{ textAlign: 'left' }}
          >
            {`Файл ${index + 1}`}
          </Button>
        ))
      )}
    </Space>
  );
});

AttachmentDownloadList.displayName = 'AttachmentDownloadList';
